import { useState, useRef } from 'react';
import {
  FileText,
  Upload,
  Loader2,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  File,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useEncompassTransactionResources, useUploadEncompassDocument } from '../../hooks/useLosProxy';

interface Props {
  transactionId: string;
}

export default function EncompassDocuments({ transactionId }: Props) {
  const { data: resources, isLoading, error, refetch, isFetching } = useEncompassTransactionResources(transactionId);
  const uploadDoc = useUploadEncompassDocument(transactionId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      uploadDoc.mutate(
        {
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          content: base64,
        },
        {
          onError: (err) => setUploadError((err as Error).message),
        },
      );
    };
    reader.readAsDataURL(file);

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (!transactionId) {
    return (
      <div className="text-center py-4">
        <p className="text-sm text-gray-500">No transaction ID available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-indigo-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Encompass Documents</h4>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn-icon p-1.5"
            title="Refresh"
          >
            <RefreshCw className={clsx('h-3.5 w-3.5', isFetching && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Upload section */}
      <div className="p-3 rounded border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/30">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.xml,.json,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
        />
        <div className="text-center">
          {uploadDoc.isPending ? (
            <div className="flex items-center gap-2 justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              <span className="text-xs text-gray-500">Uploading document...</span>
            </div>
          ) : (
            <>
              <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1" />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-blue-500 hover:text-blue-400 font-medium"
              >
                Click to upload a document
              </button>
              <p className="text-[10px] text-gray-400 mt-0.5">PDF, XML, JSON, or other document formats</p>
            </>
          )}
        </div>
      </div>

      {uploadDoc.isSuccess && (
        <div className="p-2.5 rounded bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            <p className="text-xs text-green-700 dark:text-green-300">Document uploaded successfully</p>
          </div>
        </div>
      )}

      {(uploadError || uploadDoc.isError) && (
        <div className="p-2.5 rounded bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-500/20">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            <p className="text-xs text-red-700 dark:text-red-300">
              {uploadError || (uploadDoc.error as Error).message}
            </p>
          </div>
        </div>
      )}

      {/* Resources list */}
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 justify-center text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading documents...
        </div>
      ) : error ? (
        <div className="text-center py-4">
          <AlertTriangle className="h-6 w-6 text-amber-400 mx-auto mb-1" />
          <p className="text-xs text-gray-500">Failed to load documents</p>
        </div>
      ) : !resources || resources.length === 0 ? (
        <div className="text-center py-4">
          <File className="h-6 w-6 text-gray-300 dark:text-gray-600 mx-auto mb-1" />
          <p className="text-xs text-gray-500">No documents uploaded to this transaction</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            Uploaded Resources ({resources.length})
          </p>
          {resources.map((res, i) => (
            <div
              key={res.id || i}
              className="flex items-center gap-2 p-2.5 rounded bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50"
            >
              <FileText className="h-4 w-4 text-gray-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-700 dark:text-gray-300 truncate">{res.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {res.mimeType && <span className="text-[10px] text-gray-400">{res.mimeType}</span>}
                  {res.size && <span className="text-[10px] text-gray-400">{(res.size / 1024).toFixed(1)}KB</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
